import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

// Contract interface for approval (subset of full contract)
export interface ApprovalContract {
  _id: string;
  contractName: string;
  contractType: "new" | "existing";
  dealType?: "New Business" | "Existing Business" | "Renewal";
  status: "active" | "past";
  mrr: number;
  arr: number;
  tcv: number;
  acv: number;
  startDate: Date;
  endDate: Date;
  hubspotDealId?: string;
  stripeSubscriptionId?: string;
  // ... other contract fields
}

export interface AlternativeMatch {
  subscriptionId: string;
  score: number;
  confidence: "high" | "medium" | "low";
  matchDetails: {
    domainMatch: boolean;
    lineItemsAmount?: number | null;
    subscriptionAmount?: number | null;
  };
}

export interface MatchDetails {
  domainMatch: boolean;
  lineItemsAmount?: number | null;
  subscriptionAmount?: number | null;
}

export interface ApprovalActionIndicators {
  hasMatch: boolean;
  hasSuggestedSubscription: boolean;
  canLinkExisting: boolean;
  canSearch: boolean;
  canCreateNew: boolean;
  recommendedAction: "link_existing" | "create_new" | "search_or_create";
  actionType: string;
  actionDescription: string;
}

export interface Approval {
  _id: string;
  organizationId: string;
  hubspotDealId: string;
  contractId: ApprovalContract;
  renewal?: {
    isRenewal: boolean;
    priorContractId?: string | null;
    renewalEffectiveDate?: string | null;
  };
  source?: "hubspot" | "stripe";
  stripeSubscriptionId?: string; // Best match (if found via fuzzy matching)
  suggestedSubscription?: {
    subscriptionId: string;
    status?: string;
    amount?: number;
    currency?: string;
    customerEmail?: string;
    customerName?: string;
    productName?: string;
  };
  alternativeMatches?: AlternativeMatch[];
  matchConfidence?: "high" | "medium" | "low";
  matchDetails?: MatchDetails;
  actionIndicators?: ApprovalActionIndicators;
  status: "pending" | "approved" | "denied";
  deniedReason?: string;
  approvedSubscriptionId?: string;
  approvedAt?: Date;
  deniedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApprovalsResponse {
  approvals: Approval[];
  count: number;
  settings?: {
    autoCreateSubscriptions: boolean;
  };
}

export interface ApproveApprovalRequest {
  subscriptionId?: string; // Optional: select an alternative match from alternativeMatches
}

export interface ApprovalSubscriptionSearchResult {
  id: string;
  status: string;
  customerId: string | null;
  customerEmail: string | null;
  created: number;
  currentPeriodEnd: number | null;
  itemsCount: number;
  amount: number;
  currency: string | null;
}

export interface ApprovalSubscriptionSearchResponse {
  results: ApprovalSubscriptionSearchResult[];
  count: number;
}

export interface ApproveApprovalResponse {
  success: boolean;
  approval: Approval;
  subscription?: {
    id: string;
    status: string;
  }; // Only present if auto-create is enabled and subscription was created
  message?: string; // "Approval marked as approved (subscription creation disabled)"
}

export interface DenyApprovalRequest {
  reason?: string; // Optional reason for denial
}

export interface DenyApprovalResponse {
  success: boolean;
  approval: Approval;
}

export interface UnlinkApprovalResponse {
  success: boolean;
  approval: Approval;
  message?: string;
}

export class ApprovalService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/revsync";
  }

  /**
   * Get approvals with optional status filter
   * @param status Filter by status ("pending", "approved", "denied") - defaults to "pending" if not provided
   */
  async getApprovals(
    status?: "pending" | "approved" | "denied",
    config?: AxiosRequestConfig,
  ): Promise<{ data: ApprovalsResponse; message: string }> {
    try {
      const params: Record<string, string> = {};
      if (status) {
        params.status = status;
      }

      const response = await httpService.get<ApiResponse<ApprovalsResponse>>(
        `${this.baseUrl}/approvals`,
        {
          ...config,
          params,
        },
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch approvals.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Approve an approval
   * If autoCreateSubscriptionOnClosedWon is enabled: Creates Stripe subscription and links it to the contract
   * If autoCreateSubscriptionOnClosedWon is disabled: Just marks approval as approved (no subscription created)
   * If subscriptionId is provided, links to that existing subscription instead of creating new
   */
  async approveApproval(
    approvalId: string,
    request?: ApproveApprovalRequest,
    config?: AxiosRequestConfig,
  ): Promise<{
    data: ApiResponse<ApproveApprovalResponse>;
    message: string;
    status: number;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<ApproveApprovalResponse>
      >(
        `${this.baseUrl}/approvals/${approvalId}/approve`,
        request || {},
        config,
      );

      return {
        data: response,
        message: response.message,
        status: response.status,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to approve approval.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Deny an approval
   * Marks approval as denied and saves denied combination to prevent showing it again in future reconciliations
   */
  async denyApproval(
    approvalId: string,
    request?: DenyApprovalRequest,
    config?: AxiosRequestConfig,
  ): Promise<{ data: DenyApprovalResponse; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<DenyApprovalResponse>
      >(`${this.baseUrl}/approvals/${approvalId}/deny`, request || {}, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to deny approval.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Unlink an approved approval (remove subscription link)
   */
  async unlinkApproval(
    approvalId: string,
    config?: AxiosRequestConfig,
  ): Promise<{
    data: ApiResponse<UnlinkApprovalResponse>;
    message: string;
    status: number;
  }> {
    try {
      const response = await httpService.post<
        ApiResponse<UnlinkApprovalResponse>
      >(`${this.baseUrl}/approvals/${approvalId}/unlink`, {}, config);

      return {
        data: response,
        message: response.message,
        status: response.status,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to unlink approval.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Search Stripe subscriptions for manual approval linking
   */
  async searchStripeSubscriptions(
    params: {
      subscriptionId?: string;
      customerId?: string;
      email?: string;
      query?: string;
      status?:
        | "all"
        | "active"
        | "trialing"
        | "past_due"
        | "unpaid"
        | "canceled"
        | "incomplete"
        | "incomplete_expired";
    },
    config?: AxiosRequestConfig,
  ): Promise<{ data: ApprovalSubscriptionSearchResponse; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<ApprovalSubscriptionSearchResponse>
      >(`${this.baseUrl}/approvals/subscriptions/search`, {
        ...config,
        params,
      });

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to search Stripe subscriptions.";
      throw new Error(errorMessage);
    }
  }
}

export const approvalService = new ApprovalService();
